import type { QuerySpec } from '@helios/powersync-client-core';

/* ------------------------------------------------------------------ */
/*  Clinical conversations — queries + write statements (PRD.274).     */
/*                                                                      */
/*  Conversations group voice recordings within a visit. Client-        */
/*  authored (offline-first). "At most one active per (visit, section)" */
/*  is enforced in the write workflow (complete-then-create), not a DB  */
/*  unique index — see visit_clinical_conversations for rationale.      */
/* ------------------------------------------------------------------ */

// Pure logic lives in a dependency-light module.
export {
  legacyConversationId,
  conversationIdForRecording,
  sortConversationsNewestFirst,
  selectActiveConversationId,
  defaultExpandedConversationId,
  reconcileActiveConversations,
  conversationSegmentStats,
  backfillLegacyGroups,
  formatConversationDateTime,
  markConversationSummaryAppendedStmt,
  LEGACY_CONVERSATION_PREFIX,
} from './conversation-logic';
export type {
  ConversationStatus,
  ConversationLike,
  RecordingLike,
  LegacyConversationGroup,
} from './conversation-logic';

export interface VisitClinicalConversationRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  patient_id: string | null;
  visit_id: string | null;
  appointment_id: string | null;
  section_key: string | null;
  title: string | null;
  status: string | null;
  started_at: string | null;
  completed_at: string | null;
  summary_appended_at: string | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
}

// ─── Queries ───

/** All live conversations for a visit section, newest first. */
export function qConversationsByVisitSection(
  visitId: string,
  sectionKey: string,
): QuerySpec<[string, string]> {
  return {
    key: ['visit_clinical_conversations', 'by-visit-section', visitId, sectionKey] as const,
    sql: `SELECT * FROM visit_clinical_conversations
          WHERE visit_id = ? AND section_key = ? AND deleted_at IS NULL
          ORDER BY started_at DESC`,
    params: [visitId, sectionKey],
    watch: ['visit_clinical_conversations'],
  };
}

/** All non-deleted extractions for a visit section (grouped by conversation in UI). */
export function qExtractionsByVisitSection(
  visitId: string,
  sectionKey: string,
): QuerySpec<[string, string]> {
  return {
    key: ['visit_clinical_extractions', 'by-visit-section-all', visitId, sectionKey] as const,
    sql: `SELECT * FROM visit_clinical_extractions
          WHERE visit_id = ? AND section_key = ? AND deleted_at IS NULL
          ORDER BY created_at DESC`,
    params: [visitId, sectionKey],
    watch: ['visit_clinical_extractions'],
  };
}

// ─── Write statements (executed via PowerSync db.execute) ───

export interface InsertConversationInput {
  id: string;
  orgId: string;
  clinicId: string | null;
  patientId: string;
  visitId: string;
  appointmentId: string | null;
  sectionKey: string;
  title: string | null;
  createdBy: string | null;
  now: string;
}

/** Insert a new active conversation. */
export function insertConversationStmt(input: InsertConversationInput): {
  sql: string;
  params: unknown[];
} {
  return {
    sql: `INSERT INTO visit_clinical_conversations
            (id, org_id, clinic_id, patient_id, visit_id, appointment_id,
             section_key, title, status, started_at, created_by, created_at, updated_at)
          VALUES (?, ?, ?, ?, ?, ?, ?, ?, 'active', ?, ?, ?, ?)`,
    params: [
      input.id,
      input.orgId,
      input.clinicId,
      input.patientId,
      input.visitId,
      input.appointmentId,
      input.sectionKey,
      input.title,
      input.now,
      input.createdBy,
      input.now,
      input.now,
    ],
  };
}

/** Mark a conversation completed (records completed_at). */
export function completeConversationStmt(input: { id: string; now: string }): {
  sql: string;
  params: [string, string, string];
} {
  return {
    sql: `UPDATE visit_clinical_conversations
          SET status = 'completed', completed_at = ?, updated_at = ?
          WHERE id = ? AND status = 'active'`,
    params: [input.now, input.now, input.id],
  };
}

/** Set a conversation's status (e.g. reconciliation → completed/archived). */
export function setConversationStatusStmt(input: { id: string; status: string; now: string }): {
  sql: string;
  params: [string, string, string];
} {
  return {
    sql: `UPDATE visit_clinical_conversations
          SET status = ?, updated_at = ?
          WHERE id = ?`,
    params: [input.status, input.now, input.id],
  };
}

/** Attach a recording to a conversation. */
export function attachRecordingToConversationStmt(input: {
  recordingId: string;
  conversationId: string;
  now: string;
}): { sql: string; params: [string, string, string] } {
  return {
    sql: `UPDATE visit_voice_recordings
          SET conversation_id = ?, updated_at = ?
          WHERE id = ?`,
    params: [input.conversationId, input.now, input.recordingId],
  };
}
